import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SpecSummary } from '../../types/project.js';
import * as projectApi from '../../services/project-api.js';
import { router } from '../../services/router.js';

type ViewMode = 'grid' | 'list';

@customElement('cp-spec-list')
export class CpSpecList extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;

  @state() private _specs: SpecSummary[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _viewMode: ViewMode = 'grid';
  @state() private _activeOnly = false;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('projectId') && this.projectId > 0) {
      void this._loadSpecs();
    }
  }

  private async _loadSpecs(): Promise<void> {
    if (!this.projectId) return;
    this._loading = true;
    this._error = '';

    try {
      this._specs = await projectApi.getSpecs(this.projectId);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Specs konnten nicht geladen werden';
    } finally {
      this._loading = false;
    }
  }

  private _toggleFilter(): void {
    this._activeOnly = !this._activeOnly;
  }

  private get _filteredSpecs(): SpecSummary[] {
    if (!this._activeOnly) return this._specs;
    return this._specs.filter(s => s.status !== 'complete' && s.progressPercent < 100);
  }

  private _onSpecSelect(e: CustomEvent<{ specId: string; projectId: number }>): void {
    router.navigateTo(`/projects/${e.detail.projectId}/specs/${e.detail.specId}`);
  }

  private _renderSkeleton() {
    return html`
      <div class="cp-spec-list__grid">
        ${[1, 2, 3].map(() => html`
          <div class="cp-spec-list__skeleton">
            <div class="cp-spec-list__skeleton-line cp-spec-list__skeleton-line--title"></div>
            <div class="cp-spec-list__skeleton-line cp-spec-list__skeleton-line--date"></div>
            <div class="cp-spec-list__skeleton-line cp-spec-list__skeleton-line--bar"></div>
            <div class="cp-spec-list__skeleton-line cp-spec-list__skeleton-line--count"></div>
          </div>
        `)}
      </div>
    `;
  }

  private _renderEmpty() {
    return html`
      <div class="cp-spec-list__empty">
        <p class="cp-spec-list__empty-text">Noch keine Specs vorhanden</p>
        <button class="cp-spec-list__empty-action">Neue Spec erstellen</button>
      </div>
    `;
  }

  render() {
    if (this._loading) return this._renderSkeleton();

    if (this._error) {
      return html`<p class="cp-spec-list__error">${this._error}</p>`;
    }

    if (this._specs.length === 0) return this._renderEmpty();

    const specs = this._filteredSpecs;

    return html`
      <div class="cp-spec-list">
        <div class="cp-spec-list__toolbar">
          <div class="cp-spec-list__filters">
            <button
              class="cp-spec-list__filter-btn ${this._activeOnly ? 'cp-spec-list__filter-btn--active' : ''}"
              @click=${this._toggleFilter}
            >Nur aktive</button>
          </div>
          <div class="cp-spec-list__view-toggle">
            <button
              class="cp-spec-list__toggle-btn ${this._viewMode === 'grid' ? 'cp-spec-list__toggle-btn--active' : ''}"
              @click=${() => { this._viewMode = 'grid'; }}
              title="Grid-Ansicht"
            >&#9638;</button>
            <button
              class="cp-spec-list__toggle-btn ${this._viewMode === 'list' ? 'cp-spec-list__toggle-btn--active' : ''}"
              @click=${() => { this._viewMode = 'list'; }}
              title="List-Ansicht"
            >&#9776;</button>
          </div>
        </div>

        ${specs.length === 0 && this._activeOnly ? html`
          <p class="cp-spec-list__no-results">Keine aktiven Specs gefunden.</p>
        ` : nothing}

        <div class="cp-spec-list__${this._viewMode}" @spec-select=${this._onSpecSelect}>
          ${specs.map(spec => html`
            <cp-spec-card .spec=${spec} .projectId=${this.projectId}></cp-spec-card>
          `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-spec-list': CpSpecList;
  }
}
